import { useState, useEffect } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import StatusIncidentService from '../../services/statusIncidentService';
import {
  StatusIncident,
  UptimeResponse,
  DayStatus,
  IncidentSeverity,
  AffectedComponent,
  vendorCategoryMap,
} from '../../models/statusIncident';
import { IntegrationVendor } from '../../models/enums/integrationEnums';

const mezzoLogo = process.env.PUBLIC_URL + '/Logo-Centro.png';

const StatusPage = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [activeIncidents, setActiveIncidents] = useState<StatusIncident[]>([]);
  const [uptime, setUptime] = useState<UptimeResponse | null>(null);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [incidentHistory, setIncidentHistory] = useState<StatusIncident[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const service = StatusIncidentService.getInstance();

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      loadDataSilent();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadDataSilent = async () => {
    try {
      const [incidents, uptimeData] = await Promise.all([service.getActiveIncidents(), service.getUptime()]);
      setActiveIncidents(incidents);
      setUptime(uptimeData);
    } catch (err) {
      console.error('Failed to refresh status data', err);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [incidents, uptimeData] = await Promise.all([service.getActiveIncidents(), service.getUptime()]);
      setActiveIncidents(incidents);
      setUptime(uptimeData);
    } catch (err) {
      console.error('Failed to load status data', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const history = await service.getIncidentHistory(100);
      setIncidentHistory(history);
    } catch (err) {
      console.error('Failed to load incident history', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => {
    setHistoryVisible(true);
    loadHistory();
  };

  const getOverallStatus = (): { label: string; color: string; bgColor: string } => {
    const hasCriticalMezzo = activeIncidents.some(
      (i) => i.severity === IncidentSeverity.Critical && i.affectedComponent === AffectedComponent.Mezzo && !i.endDate
    );
    const hasActiveIncidents = activeIncidents.some((i) => !i.endDate);

    if (hasCriticalMezzo) return { label: 'Major Outage', color: '#DC2626', bgColor: '#FEE2E2' };
    if (hasActiveIncidents) return { label: 'Degraded Performance', color: '#D97706', bgColor: '#FEF3C7' };
    return { label: 'All Systems Operational', color: '#059669', bgColor: '#D1FAE5' };
  };

  const getSeverityTag = (severity: IncidentSeverity) => {
    switch (severity) {
      case IncidentSeverity.Critical:
        return <Tag value="Critical" severity="danger" />;
      case IncidentSeverity.Degraded:
        return <Tag value="Degraded" severity="warning" />;
      case IncidentSeverity.Info:
        return <Tag value="Info" severity="info" />;
    }
  };

  const getBarColor = (status: DayStatus): string => {
    switch (status) {
      case DayStatus.Down:
        return '#DC2626';
      case DayStatus.Degraded:
        return '#F59E0B';
      default:
        return '#22C55E';
    }
  };

  const getBarTooltip = (status: DayStatus): string => {
    switch (status) {
      case DayStatus.Down:
        return 'Major Outage';
      case DayStatus.Degraded:
        return 'Degraded';
      default:
        return 'Operational';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (date: Date): string => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getComponentLabel = (incident: StatusIncident): string => {
    if (incident.affectedComponent === AffectedComponent.Vendor && incident.affectedVendor) {
      return incident.affectedVendor;
    }
    return incident.affectedComponent;
  };

  // Group vendors by category for the 3rd party providers section
  const vendorsByCategory = Object.entries(
    Object.values(IntegrationVendor).reduce(
      (acc, vendor) => {
        if (vendor === IntegrationVendor.None) return acc;
        const category = vendorCategoryMap[vendor] || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(vendor);
        return acc;
      },
      {} as Record<string, string[]>
    )
  );

  const getVendorStatus = (vendor: string): { label: string; icon: string; color: string; bgColor: string } => {
    const vendorIncident = activeIncidents.find(
      (i) => i.affectedComponent === AffectedComponent.Vendor && i.affectedVendor === vendor && !i.endDate
    );
    if (!vendorIncident)
      return { label: 'Operational', icon: 'pi pi-check-circle', color: '#16A34A', bgColor: '#F0FDF4' };
    if (vendorIncident.severity === IncidentSeverity.Critical)
      return { label: 'Outage', icon: 'pi pi-times-circle', color: '#DC2626', bgColor: '#FEF2F2' };
    if (vendorIncident.severity === IncidentSeverity.Degraded)
      return { label: 'Degraded', icon: 'pi pi-exclamation-triangle', color: '#D97706', bgColor: '#FFFBEB' };
    return { label: 'Info', icon: 'pi pi-info-circle', color: '#3B82F6', bgColor: '#EFF6FF' };
  };

  const uptimeTiles = uptime
    ? [
        { label: '7 Days', value: uptime.uptime7Days },
        { label: '14 Days', value: uptime.uptime14Days },
        { label: '30 Days', value: uptime.uptime30Days },
        { label: '60 Days', value: uptime.uptime60Days },
        { label: '90 Days', value: uptime.uptime90Days },
      ]
    : [];

  const ongoingIncidents = activeIncidents.filter((i) => !i.endDate);
  const recentIncidents = activeIncidents.filter((i) => !!i.endDate);

  const status = getOverallStatus();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={mezzoLogo} alt="Mezzo" className="h-8" />
            <h1 className="text-xl font-semibold text-gray-900">System Status</h1>
          </div>
          <Button
            label="Incident History"
            icon="pi pi-history"
            className="p-button-text p-button-sm"
            onClick={openHistory}
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 flex flex-col gap-4">
        {/* Overall Status Banner */}
        <div
          className="rounded-lg p-4 text-center"
          style={{ backgroundColor: status.bgColor, border: `1px solid ${status.color}30` }}
        >
          <div className="flex items-center justify-center gap-2 mb-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: status.color }}
            />
            <span className="text-lg font-semibold" style={{ color: status.color }}>
              {status.label}
            </span>
          </div>
          <p className="text-sm text-gray-600">Last updated: {formatDateTime(new Date())}</p>
        </div>

        {/* Uptime Bar - 90 Day Visual (desktop) / Summary Stats (mobile) */}
        {uptime && uptime.dailyStatuses.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Mezzo Platform</h2>
              <span
                className="text-sm font-medium px-3 py-1 rounded-full"
                style={{ backgroundColor: status.bgColor, color: status.color }}
              >
                {status.label}
              </span>
            </div>
            {/* Bar chart - hidden on mobile */}
            <div className="hidden sm:block">
              <div className="flex gap-[1px] items-stretch" style={{ height: '40px' }}>
                {uptime.dailyStatuses.map((day, idx) => (
                  <Tooltip key={idx} target={`.status-bar-${idx}`} content={`${formatDate(day.date)}: ${getBarTooltip(day.status)}`} />
                ))}
                {uptime.dailyStatuses.map((day, idx) => (
                  <div
                    key={idx}
                    className={`status-bar-${idx} flex-1 cursor-pointer transition-all hover:opacity-75`}
                    style={{
                      backgroundColor: getBarColor(day.status),
                      minWidth: '2px',
                      borderRadius: '2px',
                    }}
                    data-pr-tooltip={`${formatDate(day.date)}: ${getBarTooltip(day.status)}`}
                    data-pr-position="top"
                  />
                ))}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-xs text-gray-400">
                  {uptime.dailyStatuses.length > 0 && formatDate(uptime.dailyStatuses[0].date)}
                </span>
                <span className="text-xs text-gray-400">Today</span>
              </div>
            </div>
            {/* Mobile summary - shown only on small screens */}
            <div className="sm:hidden">
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">90-Day Uptime</span>
                  <span className="text-sm font-bold" style={{ color: uptime.uptime90Days >= 99.9 ? '#16A34A' : uptime.uptime90Days >= 99 ? '#D97706' : '#DC2626' }}>
                    {uptime.uptime90Days}%
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">30-Day Uptime</span>
                  <span className="text-sm font-bold" style={{ color: uptime.uptime30Days >= 99.9 ? '#16A34A' : uptime.uptime30Days >= 99 ? '#D97706' : '#DC2626' }}>
                    {uptime.uptime30Days}%
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600">7-Day Uptime</span>
                  <span className="text-sm font-bold" style={{ color: uptime.uptime7Days >= 99.9 ? '#16A34A' : uptime.uptime7Days >= 99 ? '#D97706' : '#DC2626' }}>
                    {uptime.uptime7Days}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Uptime Tiles */}
        {uptimeTiles.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3">
            {uptimeTiles.map((tile) => {
              const tileColor = tile.value >= 99.9 ? '#16A34A' : tile.value >= 99 ? '#D97706' : '#DC2626';
              return (
                <div
                  key={tile.label}
                  className="bg-white rounded-lg border border-gray-200 p-3 text-center"
                  style={{ minWidth: '120px', flex: '1 1 0' }}
                >
                  <div className="text-xl font-bold" style={{ color: tileColor }}>
                    {tile.value}%
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{tile.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Active Incidents */}
        {ongoingIncidents.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Active Incidents</h2>
            <div className="flex flex-col gap-3">
              {ongoingIncidents.map((incident) => (
                <div
                  key={incident.statusIncidentId}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getSeverityTag(incident.severity)}
                      <span className="font-medium text-gray-900">{incident.title}</span>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                      {getComponentLabel(incident)}
                    </span>
                  </div>
                  {incident.detail && (
                    <p className="text-sm text-gray-600 mb-2">{incident.detail}</p>
                  )}
                  <div className="text-xs text-gray-400">
                    Started: {formatDateTime(incident.startDate)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recently Resolved Incidents (closed but still shown on status page) */}
        {recentIncidents.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Recently Resolved Incidents</h2>
            <div className="flex flex-col gap-3">
              {recentIncidents.map((incident) => (
                <div
                  key={incident.statusIncidentId}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Tag value="Resolved" severity="success" />
                      {getSeverityTag(incident.severity)}
                      <span className="font-medium text-gray-900">{incident.title}</span>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                      {getComponentLabel(incident)}
                    </span>
                  </div>
                  {incident.detail && (
                    <p className="text-sm text-gray-600 mb-2">{incident.detail}</p>
                  )}
                  <div className="text-xs text-gray-400">
                    Started: {formatDateTime(incident.startDate)}
                    {incident.endDate && <> · Resolved: {formatDateTime(incident.endDate)}</>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 3rd Party Provider Status */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">3rd Party Providers</h2>
          <div className="flex flex-col gap-4">
            {vendorsByCategory.map(([category, vendors]) => (
              <div key={category}>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {category}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {vendors.map((vendor) => {
                    const vendorStatus = getVendorStatus(vendor);
                    return (
                      <div
                        key={vendor}
                        className="flex items-center gap-3 p-3 rounded-lg border border-gray-100"
                        style={{ backgroundColor: vendorStatus.bgColor }}
                      >
                        <i
                          className={vendorStatus.icon}
                          style={{ color: vendorStatus.color, fontSize: '1.25rem' }}
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium text-gray-800 truncate">{vendor}</span>
                          <span className="text-xs" style={{ color: vendorStatus.color }}>
                            {vendorStatus.label}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-2">
          <p className="text-xs text-gray-400">
            Powered by Mezzo · Status updates refresh every 60 seconds
          </p>
        </div>
      </div>

      {/* History Dialog */}
      <Dialog
        header="Incident History"
        visible={historyVisible}
        onHide={() => setHistoryVisible(false)}
        style={{ width: '700px', maxWidth: '95vw' }}
        maximizable
      >
        {historyLoading ? (
          <div className="flex justify-center py-8">
            <ProgressSpinner style={{ width: '40px', height: '40px' }} />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {incidentHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No incident history found.</p>
            ) : (
              incidentHistory.map((incident) => (
                <div
                  key={incident.statusIncidentId}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {getSeverityTag(incident.severity)}
                      <span className="font-medium text-sm text-gray-900">{incident.title}</span>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-3">
                      {getComponentLabel(incident)}
                    </span>
                  </div>
                  {incident.detail && (
                    <p className="text-xs text-gray-600 mb-1">{incident.detail}</p>
                  )}
                  <div className="text-xs text-gray-400">
                    {formatDateTime(incident.startDate)}
                    {incident.endDate && <> — {formatDateTime(incident.endDate)}</>}
                    {!incident.endDate && <span className="text-orange-500 ml-2">Ongoing</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default StatusPage;
